/**
 * Go-live checklist — `/countries/:cc/admin/go-live`. RESTORED, U4.
 *
 * ===========================================================================
 * WHY IT IS BACK, AND WHAT IS DELIBERATELY NOT BACK WITH IT
 * ===========================================================================
 * The screen was deleted on 2026-08-10 (tax-groups design §6, G2) together with its
 * route, its nav entry and `GET /admin/verify-flags` (OD-5). The owner reversed that on
 * 2026-08-14: *"I removed it and that was wrong."* The reason is measurable rather than
 * a matter of taste — production reported `verify_before_prod.products: 1` on the book of
 * the day, seeded before `117db51`, and after the
 * retirement there was NOWHERE in the product that said WHICH row. `/health` is
 * `@Public()` and answers with a bare integer; the remedy the API's own refusal sentence
 * names ("Admin → Products") is a screen with no unverified filter, so finding the row
 * meant scanning a catalogue, calling the API by hand, or running SQL on the server.
 *
 * ⚠ THE ENDPOINT DID NOT COME BACK, AND THAT IS A MEASURED DECISION, NOT A SHORTCUT.
 * `catalogue-pricing.e2e-spec.ts` (7) proves over real HTTP that `GET /products` — which
 * has shipped since the per-country split — carries `verify_before_prod` on every row,
 * that a GROUP ADMIN's flagged subset EQUALS `/health`'s counter, and that one flag flip
 * moves both together in both directions. So the listing path already existed; restoring
 * a second authenticated report to say the same thing would be the drift OD-5 removed.
 * `admin.e2e-spec.ts` still pins the retired route's 404.
 *
 * ===========================================================================
 * THE THREE QUESTIONS THIS SCREEN OWES AN OPERATOR
 * ===========================================================================
 *   WHICH rows   — country, code and the name as it currently reads.
 *   WHY each one — the NAME queue is the one flag still backed by a refusal
 *                  (`NAME_UNVERIFIED` blocks send / confirm / PDF, OD-4). The price
 *                  flag, which shares the column name on a different table, is
 *                  informational since G1 and is shown in its own section saying so.
 *   WHAT clears it — rename first if the name is a placeholder, then "Confirm the name"
 *                  on Admin → Products. Two acts, in that order, with a link to the
 *                  screen that performs them.
 *
 * ⚠ NO CONFIRM BUTTON LIVES HERE, ON PURPOSE. The products screen puts that request
 * behind a dialog stating the three honest edges (sent documents keep the old name; an
 * un-described draft line picks the new one up on re-price; future renewal letters read
 * it as it stands then). A one-click confirm on a checklist would let somebody confirm
 * a placeholder — `TBD — Angola government patch (AGT)`, the name the Angolan patch
 * shipped under until `117db51` — AS the name that prints on Angolan customer
 * documents — the exact accident the two-act design exists to prevent. The retired
 * screen was read-only for the same reason; this one stays read-only.
 *
 * ===========================================================================
 * ⚠ AN EMPTY LIST IS NOT AN ALL-CLEAR, AND THAT IS THE WHOLE FALSIFICATION
 * ===========================================================================
 * Two things can make this screen's list empty while rows ARE flagged:
 *
 *   · SCOPE. `/health` counts every country with no filter. `GET /products` returns the
 *     caller's countries — for a group admin that is all six, for a Country Admin it is
 *     theirs. A country seat can legitimately have nothing to do while the group gate
 *     stays shut on somebody else's row (measured: catalogue-pricing (7c)).
 *   · TRUNCATION. `perPage` is capped at 200. `fetchProductsForReview` pages until the
 *     server's `total` is covered and reports `complete: false` if it could not.
 *
 * So the all-clear renders ONLY when the list is empty AND the counter this screen was
 * measured against agrees. Otherwise the screen says the counters disagree and names
 * both numbers. `golive.test.tsx` pins that with a flagged row: a screen showing
 * "nothing flagged" while `/health` reads 1 must fail, and it does.
 *
 * THE VERDICT IS THE SERVER'S FIELD, never recomputed here — `/health.go_live_ready`
 * already ANDs the queue with the search probe, and a second implementation would drift
 * in the direction that matters (a green screen over a closed gate).
 *
 * ===========================================================================
 * ⚠ AND SINCE THE INDIA GO-LIVE WORK: THE SCREEN ALSO SEES ITS OWN COUNTRY
 * ===========================================================================
 * Everything above this paragraph is about ONE GROUP-WIDE COUNTER. That counter is not
 * an answer about a country, and on the data of the day it was the wrong answer about the
 * one country the owner asked about. MEASURED on `crf_v3_ta` (corrected 2026-09-07: since
 * `117db51` a fresh seed measures `AO 0`, so the `AO 1` below is the measurement as it
 * stood, not today's; the reasoning it supports is unchanged):
 *
 *     SELECT c.code, COUNT(p.id) FROM countries c
 *       LEFT JOIN products p ON p.country_id = c.id AND p.verify_before_prod = 1
 *      GROUP BY c.code   ⇒  AE 0 · AO 1 · CD 0 · IN 0 · TZ 0 · XM 0
 *
 * India has ZERO flagged names, so every counter on this screen reports nothing to do in
 * India — while `letterheadDefect` on India's stored row returns
 * `countries.address_block: unconfirmed`, `POST /quotes/:id/pdf` refuses with
 * `COUNTRY_LETTERHEAD_UNUSABLE`, and India cannot produce a quotation at all. The flag
 * counter has NO ARM that could say NOT-READY about India; it is not evidence about India
 * in either direction.
 *
 * So the screen reads `GET /countries/:cc/readiness` for the country in the URL and renders
 * that country's own verdict, BESIDE the group counter rather than instead of it. Two
 * counters, one database, two questions — and when they disagree the screen SAYS they
 * disagree rather than picking the comfortable one.
 *
 * ⚠ `can_issue_documents` IS READ, NEVER RECOMPUTED FROM `items`. Same rule as the verdict
 * above, and the same failure if it is broken: which items GATE and which are merely
 * REPORTED is the server's membership rule, and a client re-deriving it would call a shut
 * gate open the first time the server gates on something this build has not heard of.
 * `golive-model.ts` `countryVerdictOf` is the one place that reads the field.
 */

import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BellOff, CircleCheck, Flag, Info, Minus, Search, Tag, TriangleAlert } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Card, Chip, CountryBadge, EmptyState, SkeletonRows } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { COUNTRIES, COUNTRY_ORDER } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import {
  adminCatalogueKeys,
  fetchCountryCurrencies,
  fetchPriceHistory,
  fetchProductsForReview,
} from './catalogue-api';
import type { ProductsForReview } from './catalogue-api';
import { adminInfraKeys, fetchCountryReadiness, fetchHealth } from './infra-api';
import type {
  CountryReadinessDto,
  HealthReportDto,
  NotificationRulesHealthDto,
  ReadinessItemDto,
  ReadinessItemKey,
  SearchIndexHealthDto,
} from './infra-api';
// The pure rules live in `golive-model.ts` so this file exports only components —
// react-refresh's rule, measured: keeping them here took the web lint 19 → 23 warnings.
import {
  countryVerdictOf,
  flaggedNames,
  flaggedPrices,
  priceListSummaries,
  reconcile,
  verdictOf,
} from './golive-model';
import type { FlaggedPrice, PriceListSummary, Verdict } from './golive-model';
import { NoAdminAccess, errorSentence } from './states';

/* ─────────────────────────────────────────────────────────────── components ── */

const VERDICT_COPY: Record<Verdict, { title: string; box: string }> = {
  ready: { title: 'Ready to go live', box: 'bg-ok-bg text-ok-fg' },
  'not-ready': { title: 'Not ready to go live', box: 'bg-warn-bg text-warn-fg' },
  unknown: { title: 'Readiness could not be checked', box: 'bg-info-bg text-info-fg' },
};

function VerdictBanner({ verdict, detail }: { verdict: Verdict; detail: string }): React.JSX.Element {
  const { title, box } = VERDICT_COPY[verdict];
  return (
    // `role="status"` so the verdict is ANNOUNCED, not merely coloured — colour is
    // reinforcement here and never the only carrier of the answer.
    <div role="status" className={`flex items-start gap-3 rounded-md px-4 py-3.5 ${box}`}>
      <span className="mt-0.5 shrink-0">
        {verdict === 'ready' ? (
          <CircleCheck aria-hidden="true" className="size-5" />
        ) : (
          <TriangleAlert aria-hidden="true" className="size-5" />
        )}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-h3">{title}</p>
        <p className="text-body13">{detail}</p>
      </div>
    </div>
  );
}

function SearchIndexCard({ search }: { search: SearchIndexHealthDto }): React.JSX.Element {
  const tone =
    search.status === 'blind'
      ? 'bg-danger-bg text-danger-fg'
      : search.status === 'ok'
        ? 'bg-ok-bg text-ok-fg'
        : 'bg-info-bg text-info-fg';
  const heading =
    search.status === 'blind'
      ? 'Customer search is not finding new customers'
      : search.status === 'ok'
        ? 'Customer search is working'
        : 'Customer search could not be checked';

  return (
    <Card title="Customer search">
      <div className={`flex items-start gap-3 rounded-md px-4 py-3 ${tone}`}>
        <span className="mt-0.5 shrink-0">
          <Search aria-hidden="true" className="size-4" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-body13">{heading}</p>
          <p className="text-label">{search.detail}</p>
        </div>
      </div>
    </Card>
  );
}

/**
 * Notification rules — reported, never blocking.
 *
 * ⚠ `readable` IS READ BEFORE ANY COUNT. An unreadable table answers with zeros, and the
 * predecessor of this card rendered those as the confident heading "No notifications are
 * configured" — a measurement claim about rows nobody read, on the readiness screen.
 *
 * The all-clear heading states WHAT WAS COUNTED (rules switched on), not what will
 * happen: the server counts rows and has never measured delivery. Drift outranks a mute
 * in the heading, because a database still holding a rule this build retired is routing
 * by the old table — the state a skipped deployment step leaves behind, and the one thing
 * here that is not a wording question.
 */
function NotificationRulesCard({ rules }: { rules: NotificationRulesHealthDto }): React.JSX.Element {
  const unreadable = !rules.readable;
  const muted = rules.disabled > 0;
  const none = rules.enabled === 0 && rules.disabled === 0;
  const drifted = rules.retired_rules.length + rules.undeployed_rules.length > 0;
  const tone = unreadable
    ? 'bg-warn-bg text-warn-fg'
    : none
      ? 'bg-danger-bg text-danger-fg'
      : muted || drifted
        ? 'bg-warn-bg text-warn-fg'
        : 'bg-ok-bg text-ok-fg';
  const heading = unreadable
    ? 'The notification rules could not be checked'
    : none
      ? 'No notifications are configured'
      : drifted
        ? 'The routing table does not match this build'
        : muted
          ? `${String(rules.disabled)} notification${rules.disabled === 1 ? '' : 's'} switched off`
          : `All ${String(rules.enabled)} notification rules are switched on`;

  return (
    <Card title="Notifications">
      <div className={`flex items-start gap-3 rounded-md px-4 py-3 ${tone}`}>
        <span className="mt-0.5 shrink-0">
          <BellOff aria-hidden="true" className="size-4" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-body13">{heading}</p>
          <p className="text-label">{rules.detail}</p>
          {[
            { label: 'Switched off', names: rules.disabled_rules },
            { label: 'Still live here but retired in this build', names: rules.retired_rules },
            { label: 'In this build but never deployed here', names: rules.undeployed_rules },
          ]
            .filter((group) => group.names.length > 0)
            .map((group) => (
              <ul key={group.label} className="mt-1 flex flex-col gap-0.5">
                <li className="text-label font-medium">{group.label}</li>
                {group.names.map((rule) => (
                  <li key={rule} className="text-label">
                    {rule}
                  </li>
                ))}
              </ul>
            ))}
        </div>
      </div>
    </Card>
  );
}

/* ─────────────────────────────── the country's own gate (readiness) ── */

/**
 * THE HUMAN NOUN FOR EACH ITEM, AND NOTHING ELSE.
 *
 * The screen supplies the noun; the SERVER supplies whether it is satisfied, which column
 * is at fault, the defect word and the shipped refusal. That division is deliberate — a
 * client-side sentence that said *why* an item is unsatisfied would be a second reading of
 * a predicate that already ships, and the first thing to drift.
 */
const READINESS_TITLE: Readonly<Record<ReadinessItemKey, string>> = {
  letterhead: 'Registered name and address',
  default_tax_group: 'A default tax group',
  product_names: 'Product names confirmed',
  tax_registration: 'Tax registration number',
  home_state_code: 'This country’s own state code',
  tally_config: 'Tally configuration',
  saft_header: 'SAF-T (AO) header identity',
};

/**
 * ⚠ AN ITEM THIS BUILD HAS NO WORD FOR IS STILL RENDERED, BY ITS RAW KEY.
 *
 * `ReadinessItemKey` is a union COPIED from the API (`infra-api.ts`), so a seventh item
 * added on the server arrives here as a key this map has no entry for. Dropping it would be
 * exactly the silent under-reporting `flaggedNames` refuses to do one card up — and worse,
 * because a gating item dropped from the list is a reason the country is shut that the
 * screen never mentions. The raw key is ugly and honest.
 */
function readinessTitle(key: ReadinessItemKey): string {
  const titles: Readonly<Record<string, string | undefined>> = READINESS_TITLE;
  return titles[key] ?? key;
}

/**
 * The two items whose behaviour an operator CANNOT infer from the row, both measured.
 *
 * · `home_state_code` — Fork 20.
 *
 *   ⚠ RULE-5 CALLOUT (owner ruling 2026-08-26, `docs/OQ-GST-NULL-HOME-STATE.md`). This
 *   entry read: *"Its failure is SILENT … `null` gives IGST 18 — and `stateUnresolved`
 *   reads FALSE in BOTH arms … Nothing throws and nothing logs."* Both halves of that
 *   silence are closed since the ruling landed: the API now REFUSES to clear the state
 *   while an active split-mode tax group exists (and to create/reactivate one while it is
 *   unset, 422 `SPLIT_GROUP_NEEDS_HOME_STATE`), and `stateUnresolved` is computed —
 *   `homeStateCode === null` — so a null planted outside the API raises the builder's
 *   "state unresolved — showing IGST" hint instead of nothing. The note below now tells
 *   the operator the surviving truth: unset state still prices inter-state (OD-2 — it is
 *   reported, not blocked), and the ordering rule is state first, then split group.
 * · `tally_config` — Fork 16, and the note is now about WHERE the work happens rather than
 *   about it being impossible.
 *
 *   ⚠ RULE-5 CALLOUT (India go-live, cluster A4). This entry read *"`country_tally_config`
 *   has no write surface anywhere in the product, so this item cannot go green today for ANY
 *   country"*, and its note told an operator the item *"cannot be cleared from any screen
 *   today"*. The first half is FALSE since `PATCH /countries/:cc/tally-config` shipped: the
 *   row is writable and `verify_before_prod` is derived from its contents, so the item CAN
 *   go green. The second half is still true and is the whole of what the note now says —
 *   there is no SCREEN for it, so an operator reading this checklist cannot act on it here.
 *   Deleting the note outright (which the A3 handoff proposed for the day a writer existed)
 *   would have left the one item on this page with no way to make progress looking exactly
 *   like the ones that do.
 * · `saft_header` — U0 of `docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 (owner "go"
 *   2026-09-25). REPORTED on the API's own membership rule; the note says what the server's
 *   `refuses_at: null` cannot — that the SAF-T (AO) export refuses on it while no quotation
 *   does — and where the columns are filled. For a country outside the regime the server
 *   answers `applicable: false` and the row reads "Not applicable" with no note needed.
 *   RULE-5 CALLOUT (AGT U6, 2026-09-26): was five columns incl. province and postal code, and
 *   "nothing refuses on it yet … The SAF-T export (not built)"; now the three the XSD makes
 *   mandatory — U6 export refuses on exactly these — and the export is built.
 */
const READINESS_NOTE: Readonly<Partial<Record<ReadinessItemKey, string>>> = {
  home_state_code:
    'Unset, a customer in this country’s own state is quoted inter-state tax — the quote builder shows its non-blocking “state unresolved” hint, but nothing stops the document. Set the state BEFORE adopting a split-mode tax group: with one active, the state cannot be cleared, and a split-mode group cannot be created while the state is unset.',
  tally_config:
    'Writable through the API only — there is no screen for it yet, so this item cannot be cleared from here. It blocks nothing either way.',
  saft_header:
    'The tax registration number, commercial registry and city a SAF-T (AO) file header must carry. Reported only — the SAF-T (AO) export refuses without these three; quotations are not affected. Fill them under Fiscal identity (AGT) on Country settings.',
};

const COUNTRY_VERDICT_BOX: Readonly<Record<Verdict, string>> = {
  ready: 'bg-ok-bg text-ok-fg',
  'not-ready': 'bg-warn-bg text-warn-fg',
  unknown: 'bg-info-bg text-info-fg',
};

/**
 * THE STATUS HALF OF THE ROW — THREE WORDS, NOT TWO.
 *
 * "Not applicable" is the SERVER's `applicable: false` and nothing this screen derives:
 * owner ruling N-56, 2026-09-19 put that judgement in `readiness.ts`, where
 * `countries.tax_label` is read, for the same reason every other word in this sentence
 * comes off the wire — a client-side reading of a shipped predicate is the first thing to
 * drift (the note over {@link READINESS_TITLE} makes the argument in full).
 *
 * ⚠ AND IT CANNOT BE KEYED OFF `satisfied`. An inapplicable item and a satisfied one are
 * BOTH `satisfied: true` on the wire, deliberately, so that neither counter has to learn a
 * second word. A row that read `satisfied` alone would print "Satisfied ·
 * countries.tax_registration" to a DR Congo operator — a claim that a number the ruling
 * forbids had been supplied.
 */
function statusSentence(item: ReadinessItemDto): string {
  if (!item.applicable) return 'Not applicable';
  if (item.satisfied) return 'Satisfied';
  return `Not satisfied — ${item.defect ?? 'no reason given'}`;
}

function ReadinessRow({ item }: { item: ReadinessItemDto }): React.JSX.Element {
  const note = READINESS_NOTE[item.key];
  // Neutral for an item nobody has to act on BECAUSE IT DOES NOT APPLY — the green tick is
  // reserved for work somebody actually did.
  const badge = !item.applicable
    ? 'bg-subtle text-fg-secondary'
    : item.satisfied
      ? 'bg-ok-bg text-ok-fg'
      : item.gating
        ? 'bg-warn-bg text-warn-fg'
        : 'bg-subtle text-fg-secondary';

  return (
    <li className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0">
      <span
        className={`mt-px inline-flex size-6 shrink-0 items-center justify-center rounded-full ${badge}`}
      >
        {!item.applicable ? (
          <Minus aria-hidden="true" className="size-3" />
        ) : item.satisfied ? (
          <CircleCheck aria-hidden="true" className="size-3" />
        ) : item.gating ? (
          <Flag aria-hidden="true" className="size-3" />
        ) : (
          <Info aria-hidden="true" className="size-3" />
        )}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="flex flex-wrap items-center gap-2 text-body13 text-fg">
          <span className="font-medium">{readinessTitle(item.key)}</span>
          {/*
           * WHICH HALF THIS ITEM IS IN, on every row including the satisfied ones. The
           * membership rule is the whole point of the model — an operator who cannot see
           * that an outstanding item blocks nothing will either panic about it or, worse,
           * learn to ignore the ones that do block.
           */}
          <Chip>{item.gating ? 'Blocks documents' : 'Reported only'}</Chip>
        </p>
        <p className="text-label text-fg-secondary">
          {`${statusSentence(item)} · ${item.field}${
            item.observed === null ? '' : ` · measured ${String(item.observed)}`
          }`}
        </p>
        {/*
         * `refuses_at` names the route, the source site and the error code. It is on the
         * wire because `PATCH /countries/:cc` has no user interface anywhere in the
         * product, so "letterhead: unconfirmed" and nothing else leaves an operator with
         * no next move. Shown only where it is load-bearing: an unsatisfied gate.
         */}
        {item.satisfied || item.refuses_at === null ? null : (
          <p className="text-label text-fg-secondary">{`Refused at ${item.refuses_at}`}</p>
        )}
        {note === undefined ? null : <p className="text-label text-fg-secondary">{note}</p>}
      </div>
    </li>
  );
}

/**
 * CAN THE COUNTRY IN THE URL ISSUE A DOCUMENT TODAY?
 *
 * ⚠ THE VERDICT IS `countryVerdictOf(readiness)` AND NOTHING ELSE. It reads the server's
 * `can_issue_documents`; it does not walk `items`. See `golive-model.ts` for the argument
 * and `golive.test.tsx` for the incoherent-payload falsification that holds it.
 */
function CountryReadinessCard({
  countryCode,
  readiness,
  error,
  groupVerdict,
}: {
  countryCode: CountryCode;
  readiness: CountryReadinessDto | undefined;
  error: unknown;
  groupVerdict: Verdict;
}): React.JSX.Element {
  const name = COUNTRIES[countryCode].name;
  const verdict = countryVerdictOf(readiness);
  const blocking = readiness?.gating_unsatisfied ?? 0;

  const title =
    verdict === 'ready'
      ? `${name} can issue documents`
      : verdict === 'not-ready'
        ? `${name} cannot issue a document`
        : `Whether ${name} can issue a document could not be checked`;

  const detail =
    verdict === 'ready'
      ? `Every item that refuses a document in ${name} is satisfied. Anything still listed below is reported, not blocking.`
      : verdict === 'not-ready'
        ? `${String(blocking)} item${blocking === 1 ? '' : 's'} on ${name}’s own record refuses a document today. Until each one is fixed, a quotation cannot be printed for a customer in ${name}.`
        : errorSentence(
            error,
            `The readiness route did not answer, so this screen cannot say whether ${name} can issue a document. It is not saying it can.`,
          );

  return (
    <Card
      title={`Can ${name} issue a document today?`}
      action={
        readiness === undefined ? null : (
          <span className="tnum text-label text-fg-secondary">
            {`${String(readiness.gating_unsatisfied)} blocking · ${String(
              readiness.reported_unsatisfied,
            )} reported`}
          </span>
        )
      }
    >
      <div
        role="status"
        className={`flex items-start gap-3 rounded-md px-4 py-3 ${COUNTRY_VERDICT_BOX[verdict]}`}
      >
        <span className="mt-0.5 shrink-0">
          {verdict === 'ready' ? (
            <CircleCheck aria-hidden="true" className="size-4" />
          ) : (
            <TriangleAlert aria-hidden="true" className="size-4" />
          )}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-body13">{title}</p>
          <p className="text-label">{detail}</p>
        </div>
      </div>

      {/*
       * ⚠ THE DISAGREEMENT, SAID OUT LOUD, IN THE ONE DIRECTION THAT IS DANGEROUS.
       *
       * Group READY over a country that cannot print is the green-screen-over-a-closed-gate
       * shape, one level down: the banner at the top of this page is a group-wide count of
       * flagged product NAMES ANDed with a search probe, and neither of those measures the
       * letterhead, the default tax group or anything else that refuses here. MEASURED: India
       * has zero flagged names and cannot print a quotation.
       *
       * The converse (group not-ready, this country fine) is ordinary and already explained
       * by the scoped sentence further down — somebody else's row holds the group gate.
       */}
      {groupVerdict === 'ready' && verdict === 'not-ready' ? (
        <Alert tone="warning" title="The gate above is not this gate">
          <p>
            {`The group-wide banner reads ready while ${name} cannot issue a document. They are
             not the same measurement: /health counts flagged product names across every
             country and probes customer search, and neither of those is what refuses here.`}
          </p>
        </Alert>
      ) : null}

      <p className="text-label text-fg-secondary">
        The blocking half is the things a shipped route actually refuses on — each one names
        the route and the error code below. The reported half is the things nothing refuses
        on today; they are listed because nothing else in the product will ever mention them.
      </p>

      {readiness === undefined ? null : (
        <ul className="flex flex-col">
          {readiness.items.map((item) => (
            <ReadinessRow key={item.key} item={item} />
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ───────────────────────────────────────────────────────────────── the page ── */

export function GoLivePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();

  /*
   * The gate every route this screen reads enforces, kept identical to the products
   * screen's expression: `config.manage` in the ACTIVE country, group admin admitted.
   * `/health` itself needs no permission at all — but a checklist is not a health probe,
   * and the rows it lists are catalogue maintenance.
   */
  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  /**
   * WHOSE CATALOGUE THIS SCREEN IS READING. `null` — the group admin — is the un-narrowed
   * read across every country, the only scope comparable with `/health`. A country seat
   * reads their active country, which is what they can act on.
   */
  const viewCc: CountryCode | null = isGroupAdmin ? null : countryCode;

  const health = useQuery({
    queryKey: adminInfraKeys.health(),
    queryFn: ({ signal }) => fetchHealth(signal),
    enabled: permitted,
  });

  const catalogue = useQuery({
    queryKey: adminCatalogueKeys.productsForReview(viewCc ?? 'all-countries'),
    queryFn: ({ signal }) => fetchProductsForReview(viewCc, signal),
    enabled: permitted,
  });

  /**
   * THE COUNTRY IN THE URL, ALWAYS — group admin or not, and NOT `viewCc`.
   *
   * `viewCc` is `null` for a group admin because the CATALOGUE read is compared against a
   * group-wide counter and only an un-narrowed read is comparable with it. Readiness is the
   * opposite kind of question: there is no group-wide readiness and there must not be one,
   * because "the group can issue documents" would be a boolean over six different answers
   * with six different remedies. `countryCode` is the country whose admin screens this URL
   * is showing, and `assertCountryConfigAccess` on the server admits a group admin to any of
   * them — so a group admin reads the country they are looking at, which is the one they
   * came here to fix.
   */
  const readiness = useQuery({
    queryKey: adminInfraKeys.countryReadiness(countryCode),
    queryFn: ({ signal }) => fetchCountryReadiness(countryCode, signal),
    enabled: permitted,
  });

  /*
   * Price versions per country in view — one query for a country seat, six for a group
   * admin. The same fan-out and the SAME QUERY KEYS the products screen already uses, so
   * moving between the two screens costs no extra request and a re-price invalidates
   * both. Built on `/history` rather than the list route for the reason that screen
   * records: the list resolves one row per product as at today and cannot show a queued
   * change at all.
   */
  const priceCountries: readonly CountryCode[] = useMemo(
    () => (viewCc === null ? COUNTRY_ORDER : [viewCc]),
    [viewCc],
  );
  const histories = useQueries({
    queries: priceCountries.map((cc) => ({
      queryKey: adminCatalogueKeys.priceHistory(cc),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchPriceHistory(cc, signal),
      enabled: permitted,
    })),
  });
  const prices = useMemo(() => {
    const out: FlaggedPrice[] = [];
    priceCountries.forEach((cc, index) => {
      const rows = histories[index]?.data;
      if (rows === undefined) return;
      out.push(...flaggedPrices(cc, rows));
    });
    return out;
  }, [histories, priceCountries]);
  /** How many of the price reads actually answered — an unanswered one is not a zero. */
  const pricesMeasured = histories.filter((query) => query.isSuccess).length;
  /**
   * The currencies each country quotes in — so an EMPTY list still gets its line on the card.
   * The same query key the Products screen uses. If it cannot answer, the card still shows
   * every list that has rows; it just cannot name an empty one.
   */
  const currencyQueries = useQueries({
    queries: priceCountries.map((cc) => ({
      queryKey: adminCatalogueKeys.currencies(cc),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchCountryCurrencies(cc, signal),
      enabled: permitted,
      staleTime: 5 * 60_000,
    })),
  });
  /** One line per country per currency list (2026-09-22) — the "Price lists" card. */
  const priceLists = useMemo(() => {
    const out: PriceListSummary[] = [];
    priceCountries.forEach((cc, index) => {
      const rows = histories[index]?.data;
      if (rows === undefined) return;
      out.push(...priceListSummaries(cc, rows, currencyQueries[index]?.data?.currencies ?? []));
    });
    return out;
  }, [histories, currencyQueries, priceCountries]);

  if (!permitted) return <NoAdminAccess />;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Go-live checklist"
        subtitle="What still holds the gate shut, row by row — and who clears each one."
      />

      <div className="flex flex-col gap-4 px-6">
        {/*
         * ⚠ FIRST, AND OUTSIDE THE CATALOGUE'S PENDING/ERROR BRANCH ON PURPOSE.
         *
         * FIRST because this URL is `/countries/:cc/admin/go-live` — the reader came here
         * about a country, and "can this country issue a document" is the question they
         * actually have. It also puts the disagreement warning ABOVE the group banner it
         * disagrees with, rather than after the comfortable answer.
         *
         * OUTSIDE because the two reads are independent: `/countries/:cc/readiness` does
         * not touch the catalogue list route, so a catalogue failure must not take the
         * country's own gate off the screen with it. The old shape hid everything behind
         * one read's error state.
         */}
        {readiness.isPending ? (
          <Card>
            <SkeletonRows rows={3} label="Checking whether this country can issue a document" />
          </Card>
        ) : (
          <CountryReadinessCard
            countryCode={countryCode}
            readiness={readiness.data}
            error={readiness.error}
            groupVerdict={verdictOf(health.data)}
          />
        )}

        {catalogue.isPending ? (
          <Card>
            <SkeletonRows rows={4} label="Loading the checklist" />
          </Card>
        ) : catalogue.isError ? (
          <Card>
            <EmptyState
              title="The catalogue could not be read"
              description={errorSentence(
                catalogue.error,
                'The server did not answer, so this screen cannot say which rows are flagged.',
              )}
            />
          </Card>
        ) : (
          <GoLiveReport
            catalogue={catalogue.data}
            health={health.data}
            healthSettled={!health.isPending}
            groupWide={viewCc === null}
            countryCode={countryCode}
            prices={prices}
            priceLists={priceLists}
            priceCountries={priceCountries}
            pricesMeasured={pricesMeasured}
          />
        )}
      </div>
    </div>
  );
}

function GoLiveReport({
  catalogue,
  health,
  healthSettled,
  groupWide,
  countryCode,
  prices,
  priceLists,
  priceCountries,
  pricesMeasured,
}: {
  catalogue: ProductsForReview;
  health: HealthReportDto | undefined;
  healthSettled: boolean;
  groupWide: boolean;
  countryCode: CountryCode;
  prices: readonly FlaggedPrice[];
  priceLists: readonly PriceListSummary[];
  priceCountries: readonly CountryCode[];
  pricesMeasured: number;
}): React.JSX.Element {
  const names = flaggedNames(catalogue.rows);
  const counter = health?.verify_before_prod?.products ?? null;
  const verdict = verdictOf(health);
  const state = reconcile({
    listed: names.length,
    counter,
    groupWide,
    complete: catalogue.complete,
  });

  const scopeSentence = groupWide
    ? 'every country'
    : `${COUNTRIES[countryCode].name} (${countryCode})`;

  return (
    <>
      {/*
       * ⚠ RULE-5 CALLOUT — USER-VISIBLE COPY (India go-live, cluster A5; owner Forks 11+12).
       * Two of these sentences described a gate with TWO terms, and it now has three: every
       * country must also be able to issue a document.
       *
       *   · The `ready` sentence under-claimed — it said what a green meant before the
       *     letterhead term existed. Corrected, so the operator is not told that a green
       *     covers less than it does.
       *
       *   · The `counter === 0` arm is the one that mattered, and the new arm below is for a
       *     state that was almost UNREACHABLE before and is now the ordinary one. With every
       *     product name confirmed, the old last branch rendered "0 product names still
       *     unconfirmed across every country" underneath a "Not ready to go live" heading —
       *     a self-contradicting sentence that names the one thing that is NOT wrong. On a
       *     seeded database that is exactly the state the day Angola's name is entered, with
       *     six placeholder letterheads still holding the gate shut.
       *
       * ⚠ AND THE SCREEN STILL DOES NOT NAME THE COUNTRY FROM `/health`, because `/health`
       * does not know how to say it (Fork 11). It points at the readiness card, which reads
       * the authenticated per-country route and is the only thing on this page that can
       * answer "which".
       */}
      <VerdictBanner
        verdict={verdict}
        detail={
          verdict === 'ready'
            ? 'Every product name has been confirmed, customer search is working, and every country can issue a document.'
            : verdict === 'unknown'
              ? 'The public /health endpoint could not be read, so the group-wide gate is unknown. The list below is this browser’s own read and stands on its own.'
              : counter === null
                ? 'The gate is shut. The list below is what this screen could read.'
                : counter === 0
                  ? 'The gate is shut, and product names are not why — every one of them is confirmed. /health needs no sign-in, so it will not say which country is holding it; the customer-search card and the country readiness card below are where that is answered.'
                  : `${String(counter)} product name${counter === 1 ? '' : 's'} still unconfirmed across every country — /health counts them, and this screen names the ones you can see.`
        }
      />

      {/* ── the queue that is actually backed by a refusal ─────────────────── */}
      <Card
        title="Product names awaiting confirmation"
        action={
          <span className="tnum text-label text-fg-secondary">
            {`${String(names.length)} in ${scopeSentence}`}
          </span>
        }
      >
        <p className="text-label text-fg-secondary">
          A flagged name has not been reviewed — it is not necessarily a placeholder, and
          some of them are real names nobody has signed off. Whatever it says prints on the
          customer’s document, and sending, confirming or printing a quote that cites one is
          refused with NAME_UNVERIFIED.
        </p>

        {names.length === 0 ? (
          /*
           * ⚠ THE ALL-CLEAR IS GATED ON THE RECONCILIATION, NOT ON THE LIST BEING EMPTY.
           * `state === 'agree'` is the only arm in which an empty list means what an
           * all-clear says. Every other arm renders the honest sentence below it.
           */
          state === 'agree' ? (
            <EmptyState
              icon={<CircleCheck aria-hidden="true" className="size-5" />}
              title="No product name is waiting"
              description={`Every product name in ${scopeSentence} has been confirmed.`}
            />
          ) : (
            <EmptyState
              icon={<TriangleAlert aria-hidden="true" className="size-5" />}
              title="Nothing flagged HERE — and that is not the group’s answer"
              description={
                state === 'unmeasured'
                  ? `Nothing is flagged in ${scopeSentence}, but /health could not be read, so the group-wide count is unknown.`
                  : `Nothing is flagged in ${scopeSentence}, while /health counts ${String(counter ?? 0)} across every country. The rest are in countries this screen cannot read for you.`
              }
            />
          )
        ) : (
          <ul className="flex flex-col">
            {names.map((row) => (
              <li
                key={row.id}
                className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0"
              >
                <span className="mt-px inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-warn-bg text-warn-fg">
                  <Flag aria-hidden="true" className="size-3" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="flex flex-wrap items-center gap-2 text-body13 text-fg">
                    {row.country === null ? null : <CountryBadge code={row.country} />}
                    <span className="font-medium">{row.code}</span>
                    <span className="text-fg-secondary">{row.name}</span>
                  </p>
                  <p className="text-label text-fg-secondary">
                    Name not confirmed · give it its real name if this one is a placeholder,
                    then use “Confirm the name” on{' '}
                    <Link
                      to={`/countries/${row.country ?? countryCode}/admin/products`}
                      className="text-fg underline-offset-2 hover:underline"
                    >
                      Admin → Products
                    </Link>
                    {row.country === null
                      ? ''
                      : ` (${COUNTRIES[row.country].name})`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── the two ways this list can be short, said out loud ─────────────── */}
      {!healthSettled ? null : state === 'agree' ? (
        <Alert tone="info">
          <p>
            {`This list is /health’s counter: both read ${String(names.length)}.`}
          </p>
        </Alert>
      ) : state === 'scoped' ? (
        <Alert tone="info" title="Your list is not the group’s gate">
          <p>
            {`You are seeing ${scopeSentence}. /health counts every country and reads ${String(
              counter ?? 0,
            )}; a row in a country you do not administer holds the gate shut and cannot be cleared from here.`}
          </p>
        </Alert>
      ) : state === 'disagree' ? (
        <Alert tone="warning" title="Counters disagree">
          <p>
            {catalogue.complete
              ? `This screen lists ${String(names.length)} and /health reads ${String(
                  counter ?? 0,
                )}. The data moved between the two reads — refresh to reconcile.`
              : `This screen read ${String(catalogue.rows.length)} of ${String(
                  catalogue.total,
                )} catalogue rows in ${String(catalogue.pages)} request(s), so the list above may be short. /health reads ${String(counter ?? 0)}.`}
          </p>
        </Alert>
      ) : (
        <Alert tone="warning" title="The gate could not be read">
          <p>
            The /health endpoint did not answer, so there is nothing to check this list
            against. It shows what this browser could read and no more.
          </p>
        </Alert>
      )}

      {/*
        * ── THE PRICE LISTS (2026-09-22) ──────────────────────────────────────────
        * Owner: *"the go live checklist should not be empty"* and *"should not give error"*.
        * Every price was seeded at the 0.00 placeholder and nothing on this screen said so;
        * this card says, per country and per currency list, how many products are priced,
        * how many still sit at the placeholder, and how many are PRICED PER CUSTOMER — the
        * government patch, blank on purpose and never a problem. Counts only (this screen's
        * permission does not include amounts). Informational: nothing refuses a document over
        * any of it. A list that could not be read is said in words, and the page carries on.
        */}
      <Card
        title="Price lists"
        action={
          <span className="tnum text-label text-fg-secondary">
            {`${String(priceLists.length)} list${priceLists.length === 1 ? '' : 's'}`}
          </span>
        }
      >
        <p className="text-label text-fg-secondary">
          What each country’s price list holds today, one line per currency — counts only.
          A product at the zero placeholder has not been priced yet. One priced per customer
          has its price typed on each quote, which is deliberate. Nothing here stops a
          document; set prices on Admin → Products.
        </p>
        {pricesMeasured < priceCountries.length ? (
          <p className="text-label text-fg-secondary">
            {`The price lists of ${String(priceCountries.length - pricesMeasured)} of ${String(
              priceCountries.length,
            )} ${priceCountries.length === 1 ? 'country' : 'countries'} could not be read yet — they are left out below, not counted as empty.`}
          </p>
        ) : null}
        {priceLists.length === 0 ? (
          pricesMeasured === 0 ? null : (
            <p className="text-label text-fg-secondary">
              No price rows in {scopeSentence} — nothing is priced for sale yet.
            </p>
          )
        ) : (
          <ul className="flex flex-col" aria-label="Price lists">
            {priceLists.map((list) => (
              <li
                key={list.key}
                className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0"
                data-testid={`price-list-${list.key}`}
              >
                <span className="mt-px inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-subtle text-fg-secondary">
                  <Tag aria-hidden="true" className="size-3" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="flex flex-wrap items-center gap-2 text-body13 text-fg">
                    <CountryBadge code={list.country} />
                    <span className="font-medium">{list.currency} list</span>
                  </p>
                  <p className="tnum text-label text-fg-secondary">
                    {list.empty
                      ? `No prices yet — nothing is offered on ${list.currency} quotes until this list is filled.`
                      : [
                      `${String(list.priced)} priced`,
                      `${String(list.placeholder)} still at the zero placeholder`,
                      ...(list.perCustomer > 0
                        ? [`${String(list.perCustomer)} priced per customer`]
                        : []),
                      ...(list.noPriceToday > 0
                        ? [`${String(list.noPriceToday)} with no price today`]
                        : []),
                      ...(list.hidden > 0
                        ? [`${String(list.hidden)} whose amount this account cannot see`]
                        : []),
                    ].join(' · ')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── the OTHER flag of the same name, kept apart on purpose ─────────── */}
      <Card
        title="Country price rows marked “not yet reviewed”"
        action={
          <span className="tnum text-label text-fg-secondary">
            {pricesMeasured < priceCountries.length
              ? `${String(prices.length)} in ${String(pricesMeasured)} of ${String(priceCountries.length)} countries read`
              : `${String(prices.length)} open`}
          </span>
        }
      >
        <p className="text-label text-fg-secondary">
          A DIFFERENT flag on a different table (country_products), sharing the column name
          with the one above. It is informational since the pricing ruling: /health does not
          count these, nothing refuses over one, and go-live is not waiting on them. Closed
          price versions are left out — no route can clear them.
        </p>
        {prices.length === 0 ? (
          <p className="text-label text-fg-secondary">
            No open price version is marked for review in {scopeSentence}.
          </p>
        ) : (
          <ul className="flex flex-col">
            {prices.map((row) => (
              <li
                key={row.key}
                className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0"
              >
                <span className="mt-px inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-subtle text-fg-secondary">
                  <Tag aria-hidden="true" className="size-3" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="flex flex-wrap items-center gap-2 text-body13 text-fg">
                    <CountryBadge code={row.country} />
                    <span className="font-medium">{row.code}</span>
                    <span className="text-fg-secondary">{row.currency}</span>
                  </p>
                  <p className="text-label text-fg-secondary">
                    {`In force from ${row.effective_from}${
                      row.effective_to === null ? '' : ` until ${row.effective_to}`
                    } · clear it with the price on `}
                    <Link
                      to={`/countries/${row.country}/admin/products`}
                      className="text-fg underline-offset-2 hover:underline"
                    >
                      Admin → Products
                    </Link>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {health?.search_index === undefined ? null : <SearchIndexCard search={health.search_index} />}

      {health?.notification_rules === undefined ? null : (
        <NotificationRulesCard rules={health.notification_rules} />
      )}

      {/*
       * WHICH BUILD ANSWERED. One line, from the field U3 put on `/health` so the running
       * commit could be read without a shell on the server — and go-live day is when
       * "which build is this" is asked first. `unknown` is printed as `unknown`: the
       * field never guesses, and neither does this line.
       */}
      {health === undefined ? null : (
        <p className="text-label text-fg-secondary">
          {health.version.source === 'unknown'
            ? 'The deployment did not report which commit it is running (unknown).'
            : `Answered by the deployment running commit ${health.version.commit}.`}
        </p>
      )}
    </>
  );
}
